import * as store from '@attain-apps/fe/web/main/lib/store'

import type { Collection } from '../../../collection'
import type * as ecModels from '../../../models'

import type { Config } from './config'
import type { Plugin } from './plugin'
import { State } from './state'

/**
 * Core actions for this {@link Plugin}
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig<
  TTypeParams extends ecModels.TypeParams.Constraint,
>(collection: Collection<TTypeParams>, config: Config) {
  return {
    /**
     * Clears the cache
     */
    clear: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducerDeprecated: () => State.INITIAL,
    }),
    /**
     * Patches an entity in the cache
     */
    patch: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly id: ecModels.EntityId
        readonly update: (
          entity: Readonly<TTypeParams['entity']> | undefined,
        ) => TTypeParams['entity'] | undefined
      }>(),
      reducerDeprecated: (state, { id, update }) => {
        const oldEntity = state.entities[id]
        const newEntity = update(oldEntity)
        if (newEntity === undefined) {
          // We either need to remove the entity, if it exists, or do nothing,
          // if it does not exists
          return oldEntity ? State.remove(state, id) : state
        }
        const newId = collection.identify(newEntity)
        if (newId !== id) {
          // If the ID has changed, we need to remove the old entity
          state = State.remove(state, id)
        }
        // Either way, we need to insert the new entity
        return State.put(state, config, { entity: newEntity, id: newId })
      },
    }),
    /**
     * Puts the entity/entities in the cache
     */
    put: store.actions.standalone({
      data: ({
        entities,
        entity,
      }: ExactlyOneOf<{
        readonly entity: TTypeParams['entity']
        readonly entities: readonly TTypeParams['entity'][]
      }>) => ({ entities: entities ?? [entity] }),
      reducerDeprecated: (state, { entities }) =>
        State.put(
          state,
          config,
          ...entities.map(entity => ({
            entity,
            id: collection.identify(entity),
          })),
        ),
    }),
    /**
     * Removes specified id/ids
     */
    remove: store.actions.standalone({
      data: ({
        id,
        ids,
      }: ExactlyOneOf<{
        readonly id: ecModels.EntityId
        readonly ids: readonly ecModels.EntityId[]
      }>) => ({ ids: ids ?? [id] }),
      reducerDeprecated: (state, { ids }) => State.remove(state, ...ids),
    }),
  } as const satisfies store.actions.Config.Constraint<State<TTypeParams>>
}
